'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiCheckboxCircleLine,
  RiEyeLine,
  RiInformationLine,
  RiTimerLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { dur, ease, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

import './autopilot.css';

/**
 * Otopilot sayfasının gösterimi — "aynı gece, iki farklı sabah".
 *
 * Örnek bir hesabın gecesi beş satırlık bir zaman çizelgesi. Ziyaretçi modu
 * değiştiriyor (Öner ↔ Otopilot); OLAYLAR aynı kalıyor, değişen yalnız
 * Operatör'ün her olayda ne yaptığı. Bölümün tek cümlesi bu: mod, gece ne
 * olacağını değil, sabah ne bulacağınızı belirler.
 *
 * Senaryo ürünün gerçek kurallarından (`@mentorivo/shared/platform-ops`
 * `policy.ts`) kuruldu, uydurma bir "AI her şeyi yapar" vaadi değil:
 *   · okuma her modda onaysız,
 *   · Otopilot'ta AZALTMA onaysız uygulanıyor,
 *   · Otopilot'ta bile adım sınırını (%25, varsayılan) aşan ARTIŞ onaya düşüyor,
 *   · reddedilen reklamın düzeltmesi iki modda da onay bekliyor.
 * Rakamlar örnek — not satırı bunu söylüyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * • Anahtar: seçili modun zemini düğmeden düğmeye KAYIYOR (`layoutId`) —
 *   "aynı anahtar, öbür konum".
 * • Mod değişince çizelgenin üstünden yukarıdan aşağı tek bir tarama geçiyor:
 *   "aynı gece baştan oynatıldı". Döngü değil, her değişimde bir kez.
 * • Sonuç satırları bulanıktan netleşerek değişiyor; DEĞİŞMEYEN satırlar
 *   yerinde duruyor — göz yalnız farka gidiyor.
 */

const EVENTS = ['patrol', 'spike', 'surge', 'rejected', 'morning'] as const;
type EventKey = (typeof EVENTS)[number];

const MODES = ['suggest', 'autopilot'] as const;
type Mode = (typeof MODES)[number];

type State = 'read' | 'wait' | 'done';

/** Her olayın iki moddaki sonucu — senaryonun omurgası (başlıktaki kurallar). */
const OUTCOME: Record<EventKey, Record<Mode, State>> = {
  patrol: { suggest: 'read', autopilot: 'read' },
  spike: { suggest: 'wait', autopilot: 'done' },
  surge: { suggest: 'wait', autopilot: 'wait' },
  rejected: { suggest: 'wait', autopilot: 'wait' },
  morning: { suggest: 'read', autopilot: 'read' },
};

/**
 * Sonuç METNİ moda göre değişiyor mu. Durumdan türetilemiyor: Meta artışı iki
 * modda da onaya düşüyor ama gerekçesi farklı (Öner'de "her şey sorulur",
 * Otopilot'ta "adım sınırını aşıyor").
 */
const VARIES: Record<EventKey, boolean> = {
  patrol: false,
  spike: true,
  surge: true,
  rejected: false,
  morning: true,
};

/** Satırdaki küçük platform işareti (beyaz zemin sürümleri). */
const MARK: Partial<Record<EventKey, { src: string; w: number; h: number }>> = {
  spike: { src: '/logos/brands/tiktok.svg', w: 352, h: 399 },
  surge: { src: '/logos/brands/meta.svg', w: 256, h: 171 },
  rejected: { src: '/logos/google-ads.svg', w: 26, h: 26 },
};

const STATE_ICON = {
  read: RiEyeLine,
  wait: RiTimerLine,
  done: RiCheckboxCircleLine,
} as const;

const outcomeV: Variants = {
  hidden: { opacity: 0, y: 4, filter: 'blur(3px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
  exit: { opacity: 0, filter: 'blur(2px)', transition: { duration: dur.instant } },
};

export function AutopilotNight() {
  const t = useTranslations('featuresAutopilot.night');
  const uid = useId();
  const [mode, setMode] = useState<Mode>('suggest');
  /* Tarama yalnız DEĞİŞİMDE: ilk çizimde 0 (sunucuyla aynı ağaç). */
  const [sweep, setSweep] = useState(0);

  const choose = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setSweep((n) => n + 1);
  };

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <SectionHead
        id={`${uid}-title`}
        eyebrow={t('eyebrow')}
        title={t.rich('title', { hl })}
        lead={t('lead')}
      />

      <div className="apn">
        <div className="apn-bar">
          <div className="apn-switch" role="group" aria-label={t('switchLabel')}>
            {MODES.map((key) => (
              <button
                key={key}
                type="button"
                className="apn-switch-btn"
                aria-pressed={mode === key}
                aria-controls={`${uid}-list`}
                onClick={() => choose(key)}
              >
                {mode === key && (
                  <motion.span
                    layoutId={`${uid}-pill`}
                    className="apn-switch-pill"
                    transition={spring.snap}
                    aria-hidden="true"
                  />
                )}
                <span className="apn-switch-label">{t(`modes.${key}.name`)}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={mode}
              className="apn-mode-note"
              variants={outcomeV}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              {t(`modes.${mode}.note`)}
            </motion.p>
          </AnimatePresence>
        </div>

        <ol className="apn-list" id={`${uid}-list`} aria-live="polite">
          {sweep > 0 && (
            <motion.span
              key={sweep}
              className="apn-scan"
              aria-hidden="true"
              initial={{ y: '-100%', opacity: 0 }}
              animate={{ y: '100%', opacity: [0, 1, 1, 0] }}
              transition={{
                duration: dur.slow * 1.4,
                ease: ease.out,
                opacity: { duration: dur.slow * 1.4, times: [0, 0.1, 0.8, 1] },
              }}
            />
          )}

          {EVENTS.map((key) => {
            const state = OUTCOME[key][mode];
            const changes = VARIES[key];
            const Icon = STATE_ICON[state];
            const mark = MARK[key];

            return (
              <li key={key} className="apn-row" data-state={state}>
                <span className="apn-time">{t(`events.${key}.time`)}</span>
                <span className="apn-node" aria-hidden="true" />

                <div className="apn-body">
                  <p className="apn-title">
                    {mark ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        className="apn-mark"
                        src={mark.src}
                        alt=""
                        width={mark.w}
                        height={mark.h}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : null}
                    {t(`events.${key}.title`)}
                  </p>
                  <p className="apn-detail">{t(`events.${key}.detail`)}</p>

                  <div className="apn-outcome">
                    {/* Modla değişmeyen satırın anahtarı sabit: yeniden
                        kurulmuyor, göz yalnız değişen satırlara gidiyor. */}
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.p
                        key={changes ? `${key}-${mode}` : key}
                        className="apn-result"
                        variants={outcomeV}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                      >
                        <span className={`apn-tag apn-tag--${state}`}>
                          <Icon size={14} aria-hidden="true" />
                          {t(`tags.${state}`)}
                        </span>
                        <span className="apn-result-text">
                          {t(`events.${key}.outcome.${changes ? mode : 'suggest'}`)}
                        </span>
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <p className="ps-note">
        <RiInformationLine size={16} aria-hidden="true" />
        {t('note')}
      </p>
    </ProductSection>
  );
}
